import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/Button'
import { WarningIcon } from './ui/WarningIcon'
import { ConfirmSummary } from './ui/ConfirmSummary'

// Generic tenant/account-impact confirmation, matching the prototype's
// `showAdminConfirm` / `#admin-confirm-modal` (subscription-portal.html): a
// red impact callout (`--danger-bg` ground, `--danger` border and icon, the
// bold impact title and its description both in the text colour), an
// optional summary table (label left, bold value right, one fact per row —
// the `.confirm-summary` construction ConfirmPurchaseModal also draws), an
// "I confirm I am authorized..." checkbox gate, and a danger confirm button
// beside a ghost Cancel. Reused by every account-wide financial or access
// change rather than rebuilt per caller.
//
// Unlike the prototype's fire-and-forget modal it waits for the server, as
// ConfirmPurchaseModal does: while the write is in flight (`pending`) Confirm,
// Cancel and the backdrop are inert, so a second click cannot run the write
// twice. `confirmDisabled` holds Confirm for a fact the summary still lacks
// (ARCHITECTURE_DECISIONS.md §45).
export function AdminConfirmModal({
  title,
  subtitle,
  impactTitle,
  impactText,
  bullets,
  checkLabel,
  confirmLabel,
  pending = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: Readonly<{
  title: string
  subtitle?: string
  /** The bold first line of the callout (`impactTitle`). */
  impactTitle?: string
  impactText: string
  /** The summary rows, `[label, value]` (`bullets`). */
  bullets?: readonly (readonly [string, string])[]
  checkLabel: string
  confirmLabel: string
  /** The confirmed write is in flight. */
  pending?: boolean
  /** Confirm stays inert even when the box is ticked. */
  confirmDisabled?: boolean
  onConfirm: () => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(false)

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000]">
      {/* The backdrop is a sibling of the dialog, not its parent, so a click
          inside the dialog never reaches it. It is a pointer convenience —
          keyboard and screen-reader users close with Cancel — hence hidden
          from both, and inert while the write is in flight. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="admin-confirm-backdrop"
        disabled={pending}
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onCancel}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby="admin-confirm-title"
        className="relative m-0 p-0 border-0 text-inherit bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-md)] w-[460px] max-w-[90vw] overflow-hidden"
      >
        <div className="px-6 pt-5 pb-4 border-b border-[var(--border-light)]">
          <h3 id="admin-confirm-title" className="text-[16px] font-semibold text-[var(--text-primary)]">
            {title}
          </h3>
          {subtitle && <p className="text-[12px] text-[var(--text-tertiary)] mt-1">{subtitle}</p>}
        </div>
        <div className="px-6 py-5">
          <div
            data-testid="admin-confirm-alert"
            className="flex gap-3 items-start bg-[var(--danger-bg)] border border-[var(--danger)] rounded-lg px-4 py-3.5 mb-3.5 text-[13px] leading-[1.6]"
          >
            <WarningIcon size={20} stroke="var(--danger)" className="shrink-0 mt-px" />
            <div>
              {impactTitle && <div className="font-bold text-[var(--text-primary)] mb-1">{impactTitle}</div>}
              <div className="text-[var(--text-primary)]">{impactText}</div>
            </div>
          </div>
          {bullets && bullets.length > 0 && (
            <ConfirmSummary rows={bullets.map(([label, value]) => ({ label, value }))} className="mb-3.5" />
          )}
          <label className="flex gap-2.5 items-start text-[13px] text-[var(--text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-[3px] accent-[var(--accent)]"
            />
            <span>{checkLabel}</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border-light)] flex justify-end gap-2">
          <Button variant="ghost" disabled={pending} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" disabled={!checked || pending || confirmDisabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </dialog>
    </div>
  )
}
